import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase, Nomination, Category } from '../../lib/supabase';
import { useCategories } from '../../hooks/useCategories';
import NominationDetails from '../nominations/NominationDetails';
import Dialog, { DialogVariant } from '../ui/Dialog';
import AdminSectionSwitch from '../admin/AdminSectionSwitch';
import './Dashboard.css';

// Estados válidos para el filtro vía query string (?status=...)
const VALID_STATUS = new Set(['all', 'pending', 'approved', 'rejected']);

const NovagobDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Lee ?status=... y ?category=... de la URL para poder enlazar
  // directamente desde correos / WhatsApp con un filtro ya aplicado.
  const initialStatus = (() => {
    const s = searchParams.get('status') || 'all';
    return VALID_STATUS.has(s) ? s : 'all';
  })();
  const initialCategory = searchParams.get('category') || 'all';

  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);
  const [selectedStatus, setSelectedStatus] = useState<string>(initialStatus);

  // Mantenemos la URL sincronizada con los filtros actuales.
  // Así el usuario puede compartir el enlace con el filtro aplicado
  // y el botón de retroceso del navegador respeta el estado.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedStatus === 'all') next.delete('status');
    else next.set('status', selectedStatus);
    if (selectedCategory === 'all') next.delete('category');
    else next.set('category', selectedCategory);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, selectedCategory]);
  const [selectedNomination, setSelectedNomination] = useState<Nomination | null>(null);
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState<any>({});
  const [dialog, setDialog] = useState<{ open: boolean; title?: string; message: string; variant?: DialogVariant }>({ open: false, message: '' });
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  // Modal de rechazo: pide un mensaje que se enviará por correo a la persona
  const [rejectModal, setRejectModal] = useState<{ open: boolean; nominationId: string | null; reason: string; submitting: boolean }>({
    open: false,
    nominationId: null,
    reason: '',
    submitting: false,
  });
  const openRejectModal = (nominationId: string) =>
    setRejectModal({ open: true, nominationId, reason: '', submitting: false });
  const closeRejectModal = () =>
    setRejectModal({ open: false, nominationId: null, reason: '', submitting: false });

  // Modal de confirmación de aprobación
  const [approveModal, setApproveModal] = useState<{ open: boolean; nominationId: string | null; submitting: boolean }>({
    open: false,
    nominationId: null,
    submitting: false,
  });
  const openApproveModal = (nominationId: string) =>
    setApproveModal({ open: true, nominationId, submitting: false });
  const closeApproveModal = () =>
    setApproveModal({ open: false, nominationId: null, submitting: false });

  const { categories } = useCategories();

  useEffect(() => {
    loadNominations();

    const nominationChannel = supabase.channel('public:nominations');

    nominationChannel
      .on('postgres_changes', { event: 'INSERT', schema: 'premios', table: 'nominations' }, () => {
        loadNominations();
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'premios', table: 'nominations' }, () => {
        loadNominations();
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'premios', table: 'nominations' }, () => {
        loadNominations();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(nominationChannel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedStatus]);
  const loadNominations = async () => {
    try {
      setLoading(true);
      let query = supabase.schema('premios').from('nominations').select('*');

      if (selectedCategory !== 'all') {
        query = query.eq('category_id', selectedCategory);
      }

      if (selectedStatus !== 'all') {
        query = query.eq('status', selectedStatus);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      const nominationsData = data || [];
      setNominations(nominationsData);
    } catch (error) {
      console.error('Error loading nominations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRankings = async (categoryId: string) => {
    try {
      const { data, error } = await supabase.rpc('premios.get_category_ranking', {
        category_uuid: categoryId,
      });

      if (error) throw error;
      setRankings((prev: any) => ({ ...prev, [categoryId]: data || [] }));
    } catch (error) {
      console.error('Error loading rankings:', error);
    }
  };

  const handleApprove = (nominationId: string) => {
    // Abre el modal — la aprobación definitiva se hace en confirmApproval
    openApproveModal(nominationId);
  };

  const confirmApproval = async () => {
    if (!approveModal.nominationId) return;
    setApproveModal((prev) => ({ ...prev, submitting: true }));
    try {
      const { error } = await supabase
        .schema('premios')
        .from('nominations')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', approveModal.nominationId);

      if (error) throw error;
      loadNominations();
      setSelectedNomination(null);
      closeApproveModal();
    } catch (error) {
      console.error('Error approving nomination:', error);
      setApproveModal((prev) => ({ ...prev, submitting: false }));
      setDialog({ open: true, title: 'Error', message: 'Error al aprobar la candidatura', variant: 'error' });
    }
  };

  const handleReject = (nominationId: string) => {
    // Abre el modal — el rechazo definitivo se hace en confirmRejection
    openRejectModal(nominationId);
  };

  const confirmRejection = async () => {
    if (!rejectModal.nominationId) return;
    const reason = rejectModal.reason.trim();
    if (!reason) return;

    setRejectModal((prev) => ({ ...prev, submitting: true }));
    try {
      const { error } = await supabase
        .schema('premios')
        .from('nominations')
        .update({
          status: 'rejected',
          rejection_reason: reason,
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', rejectModal.nominationId);

      if (error) throw error;
      loadNominations();
      setSelectedNomination(null);
      closeRejectModal();
      setDialog({
        open: true,
        title: 'Candidatura rechazada',
        message: 'Se ha enviado el mensaje por correo a la persona que la presentó.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error rejecting nomination:', error);
      setRejectModal((prev) => ({ ...prev, submitting: false }));
      setDialog({ open: true, title: 'Error', message: 'Error al rechazar la candidatura', variant: 'error' });
    }
  };

  const getCategoryById = (id: string): Category | undefined => {
    return categories.find((c) => c.id === id);
  };

  const pendingCount = nominations.filter((n) => n.status === 'pending').length;
  const approvedCount = nominations.filter((n) => n.status === 'approved').length;
  const rejectedCount = nominations.filter((n) => n.status === 'rejected').length;

  return (
    <div className="container dashboard">
      <div
        className="dashboard-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}
      >
        <div>
          <h1>Panel de Administración</h1>
          <p>Gestión de candidaturas</p>
        </div>
        <AdminSectionSwitch />
      </div>

      {/* Estadísticas */}
      <div className="grid grid-3">
        <div className="stat-card">
          <div className="stat-value">{pendingCount}</div>
          <div className="stat-label">Pendientes</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{approvedCount}</div>
          <div className="stat-label">Aprobadas</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{rejectedCount}</div>
          <div className="stat-label">Rechazadas</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="filters">
          <div className="form-group">
            <label className="form-label">Categoría</label>
            <select
              className="form-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">Todas las categorías</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name} ({cat.current_nominations}/{cat.max_nominations})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Estado</label>
            <select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="approved">Aprobadas</option>
              <option value="rejected">Rechazadas</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">&nbsp;</label>
            <button className="btn btn-secondary" onClick={loadNominations}>
              Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* Candidaturas por categoría */}
      {categories.map((category) => {
        const categoryNominations = nominations.filter(
          (n) => n.category_id === category.id
        );

        if (categoryNominations.length === 0) return null;

        return (
          <div key={category.id} className="card" style={{ marginTop: '24px' }}>
            <div className="category-section">
              <div className="category-header">
                <h2>{category.name}</h2>
                <div className="category-meta">
                  <span className="category-count">
                    {categoryNominations.length}/{category.max_nominations} candidaturas
                  </span>
                  {category.type === 'project' && (
                    <span className="badge badge-warning">Proyecto</span>
                  )}
                </div>
              </div>

              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${(categoryNominations.length / category.max_nominations) * 100}%`,
                  }}
                ></div>
              </div>

              {category.current_nominations > 0 && (
                <button
                  className="btn btn-secondary"
                  onClick={() => loadRankings(category.id)}
                  style={{ marginTop: '12px' }}
                >
                  Ver Ranking
                </button>
              )}

              {rankings[category.id] && (
                <div className="ranking-section">
                  <h3>Ranking actual</h3>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Posición</th>
                        <th>Candidato/Proyecto</th>
                        <th>Puntuación Media</th>
                        <th>Votos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankings[category.id].map((item: any, index: number) => (
                        <tr key={item.nomination_id}>
                          <td>
                            <strong>#{item.rank}</strong>
                          </td>
                          <td>{item.candidate_name || item.project_title}</td>
                          <td>
                            <span className="score-badge">
                              {item.average_score?.toFixed(2) || '0.00'}
                            </span>
                          </td>
                          <td>{item.total_votes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <table className="table table-responsive">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Candidat@/Proyecto</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryNominations.map((nom) => (
                    <tr key={nom.id}>
                      <td data-label="Fecha">{new Date(nom.created_at).toLocaleDateString()}</td>
                      <td data-label="Candidat@/Proyecto">
                        <div>{nom.candidate_name || nom.project_title}</div>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                          {nom.submitter_email}
                        </div>
                      </td>
                      <td data-label="Estado">
                        <span className={`badge badge-${nom.status}`}>
                          {nom.status === 'pending' && 'Pendiente'}
                          {nom.status === 'approved' && 'Aprobada'}
                          {nom.status === 'rejected' && 'Rechazada'}
                        </span>
                      </td>
                      <td data-label="Acciones">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedNomination(nom)}
                        >
                          Ver detalles
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      )}

      {!loading && nominations.length === 0 && (
        <div className="card" style={{ marginTop: '24px', textAlign: 'center' }}>
          <p>No hay candidaturas que mostrar</p>
        </div>
      )}

      {/* Modal de detalles */}
      {selectedNomination && (
        <div className="modal-overlay" onClick={() => setSelectedNomination(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <NominationDetails
              nomination={selectedNomination}
              category={getCategoryById(selectedNomination.category_id)}
              onClose={() => setSelectedNomination(null)}
              onApprove={() => handleApprove(selectedNomination.id)}
              onReject={() => handleReject(selectedNomination.id)}
              showActions={true}
            />
          </div>
        </div>
      )}
      <Dialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        onClose={closeDialog}
      />

      {/* Modal de confirmación de aprobación */}
      {approveModal.open && (
        <div
          className="modal-overlay"
          onClick={() => !approveModal.submitting && closeApproveModal()}
          style={{ zIndex: 2100 }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480 }}
          >
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ margin: 0, color: 'var(--color-success, #10b981)' }}>
                Aprobar candidatura
              </h2>
              <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)', fontSize: 14, lineHeight: 1.5 }}>
                ¿Estás seguro de que quieres aprobar esta candidatura? Pasará a estar
                disponible para la fase de votación del Jurado.
              </p>
            </div>

            <div className="modal-footer" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeApproveModal}
                disabled={approveModal.submitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={confirmApproval}
                disabled={approveModal.submitting}
              >
                {approveModal.submitting ? 'Aprobando…' : 'Sí, aprobar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rechazo — pide un mensaje que se enviará por correo */}
      {rejectModal.open && (
        <div
          className="modal-overlay"
          onClick={() => !rejectModal.submitting && closeRejectModal()}
          style={{ zIndex: 2100 }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}
          >
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ margin: 0, color: 'var(--color-error)' }}>Rechazar candidatura</h2>
              <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)', fontSize: 14, lineHeight: 1.5 }}>
                Escribe el mensaje que recibirá la persona que presentó la candidatura.
                Este texto se incluirá <strong style={{ color: 'var(--color-text)' }}>literalmente en el correo</strong> que se le enviará automáticamente desde la plataforma.
              </p>
            </div>

            <div
              style={{
                padding: '10px 14px',
                borderRadius: 10,
                background: 'rgba(255,215,2,0.06)',
                border: '1px solid rgba(255,215,2,0.3)',
                color: '#fde68a',
                fontSize: 13,
                marginBottom: 16,
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>📧</span>
              <span>
                Lo que escribas aquí <strong>llegará por correo</strong> a la persona postulante,
                tal cual lo redactes. Sé claro y respetuoso.
              </span>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="rejection-reason-textarea">
                Mensaje para la persona postulante *
              </label>
              <textarea
                id="rejection-reason-textarea"
                className="form-textarea"
                rows={6}
                value={rejectModal.reason}
                onChange={(e) =>
                  setRejectModal((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder="Ej. Tras revisar tu candidatura, el Jurado considera que no cumple el criterio X de las bases..."
                disabled={rejectModal.submitting}
                autoFocus
                required
              />
              <div className="form-helper" style={{ fontSize: 12, marginTop: 6 }}>
                {rejectModal.reason.trim().length === 0
                  ? 'El mensaje es obligatorio.'
                  : `${rejectModal.reason.trim().length} caracteres`}
              </div>
            </div>

            <div className="modal-footer" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRejectModal}
                disabled={rejectModal.submitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmRejection}
                disabled={rejectModal.submitting || rejectModal.reason.trim().length === 0}
              >
                {rejectModal.submitting
                  ? 'Rechazando…'
                  : 'Rechazar y enviar correo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NovagobDashboard;
